import { GuideSettings } from "@common/models";
import type { FlowFlexibility, PyneSettings, TourPlayback } from "@common/schemas";
import { debounce, merge, pickBy } from "lodash-es";
import { autorun, makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import { debugLog, log } from "../../log";
import { getUrl, navigateTo, reloadWindow } from "../../window";
import { AvatarManager, SectionMediaUrls } from "./avatarManager";
import { FlowHookActionsFacade, StepActionsFacade } from "./facades";
import { CreateSystemFlowsParams, FlowKey, systemFlowTypes } from "./flow";
import { MultipleChoiceModalStep } from "../steps/v2/modal";
import { MultipleChoiceQuestionStep } from "../steps/v2/question";
import { Step } from "../steps/v2/step";
import { AvatarStateStorage, buildInMemoryAvatarStateStorage } from "../persistent-storage/avatarState";
import { TourStateStorage, buildInMemoryTourStateStorage } from "../persistent-storage/tourState";
import { TourStartStrategy } from "../tourStartManager";
import { TourTrackManager } from "../tourTrackManager";
import { fromStream } from "mobx-utils";
import { createFlowStateActor, createFlowStateStream } from "./flowStateMachine";
import {
  TourPlaybackEventContext,
  TourPlaybackManagerEvents,
  TourPlaybackNamespace,
} from "./tourPlaybackManager.events";
import { Checkpoint, Flow, FlowPosition, FlowState, Section, SuspendReason } from "./types";
import { buildAllFlows } from "./build";
import { globalEventBus } from "@/lib/events/eventBus";
import { EventEmitter } from "@/lib/events/eventEmitter";
import { buildInMemoryUserStateStorage, UserStateStorage } from "../persistent-storage/userState";
import { waitForOnTrackConditions } from "../onTrackConditionCheck";
import { pageInfo } from "@/stores/PageInfo";
import { TourFinishManager } from "./tourFinishManager";
import { TourEngagedManager } from "./tourEngagedManager";

export interface TourPlaybackManagerParams {
  pyneSettings: PyneSettings;
  guideId: string;
  organizationId: string;
  pyneClient?: PyneClient;
  sessionId: string;
  startedAt: number;
  tourPlayback: TourPlayback;
  settings: GuideSettings;
  tourStateStorage?: TourStateStorage;
  userStateStorage?: UserStateStorage;
  avatarStateStorage?: AvatarStateStorage;
  startFromStepId?: string;
  shouldAutoPlayFirstStep?: boolean;
  skipBlobMediaPreload?: boolean;
  startStrategy?: TourStartStrategy;
  mediaPlaybackRate?: number;
  tabId: string;
}

interface PyneClient {
  currentUser: { id: string; traits: Record<string, any> } | null;
  anonymousId: string | null;
}

type FeedbackSource = "widget" | "website" | "question" | "modal" | "additionalContent" | "unknown";

export type SubtitleText = {
  word: string;
  isSpoken: boolean;
};

export class TourPlaybackManager {
  pyneSettings: PyneSettings;
  guideId: string;
  organizationId: string;
  pyneClient?: PyneClient;
  tourPlayback: TourPlayback;
  flows: Record<FlowKey, Flow>;
  avatarManager: AvatarManager;
  settings: GuideSettings;
  private pageInfo = pageInfo;
  tourStateStorage: TourStateStorage;
  userStateStorage: UserStateStorage;
  tourTrackManager: TourTrackManager;
  startFromStepId?: string;
  startStrategy: TourStartStrategy;
  tourFinishManager: TourFinishManager;
  tourEngagedManager: TourEngagedManager;
  flowFlexibility: FlowFlexibility;
  shouldAutoPlayFirstStep?: boolean;
  events = new EventEmitter<typeof TourPlaybackNamespace, TourPlaybackManagerEvents, TourPlaybackEventContext>(
    TourPlaybackNamespace
  );
  tabId: string;
  startedAt: number;
  sessionId: string;

  status: "initialized" | "started" | "suspended" | "finished" | "dismissed" | "terminated" = "initialized";

  _currentTargetElement?: Element | null;
  currentFlowKey?: FlowKey;
  currentFlow?: Flow;
  currentTime: number = 0;

  readonly initialFlowState: FlowState = {
    currentTime: 0,
    currentSectionIndex: 0,
    currentStepIndex: 0,
  };

  readonly defaultStartStrategy: TourStartStrategy = { type: "startOver" };

  flowStateStack: FlowState[] = [];
  checkpoints: Checkpoint[] = [];

  isWaitingForIntro = false;

  flowStateActor?: ReturnType<typeof createFlowStateActor>;
  flowStateStream?: ReturnType<typeof createFlowStateStream>;

  shortClipMediaUrl?: string;

  stopGlobalEventBusMiddleware?: () => void;
  stopListeningPageUrlChange?: () => void;

  constructor(params: TourPlaybackManagerParams) {
    makeAutoObservable(this);

    this.pyneSettings = params.pyneSettings;
    this.guideId = params.guideId;
    this.organizationId = params.organizationId;
    this.tourPlayback = params.tourPlayback;
    this.startStrategy = params.startStrategy ?? this.defaultStartStrategy;
    this.settings = params.settings;
    this.tourStateStorage = params.tourStateStorage ?? buildInMemoryTourStateStorage();
    this.userStateStorage = params.userStateStorage ?? buildInMemoryUserStateStorage();
    this.tourTrackManager = new TourTrackManager();
    this.tourFinishManager = new TourFinishManager();
    this.flowFlexibility = this.settings.flow_flexibility;
    this.flows = this.buildFlows();
    this.isWaitingForIntro = this.getWaitingForIntro();
    this.tabId = params.tabId;
    this.tourEngagedManager = new TourEngagedManager(this.tourPlayback);
    this.pyneClient = params.pyneClient;
    this.sessionId = params.sessionId;
    this.startedAt = params.startedAt;
    const sectionMedias = this.tourPlayback.sections.map((section) => {
      return { sectionId: section.id, mediaUrl: section.avatarMediaUrl, mediaDuration: section.mediaDuration };
    });

    const systemFlows = pickBy(this.flows, (flow) => systemFlowTypes.includes(flow.key as any));

    const systemFlowMedias = Object.entries(systemFlows).reduce((acc, [key, value]) => {
      value.sections.forEach((section) => {
        if (section.avatarMediaUrl) {
          acc[section.id] = section.avatarMediaUrl;
        }
      });
      return acc;
    }, {} as SectionMediaUrls);

    this.avatarManager = new AvatarManager({
      sectionMedias: sectionMedias,
      sytemFlowMediaUrls: systemFlowMedias,
      stateStorage: params.avatarStateStorage ?? buildInMemoryAvatarStateStorage(),
      skipBlobMediaPreload: params.skipBlobMediaPreload,
      playbackRate: params.mediaPlaybackRate,
      mediaAmplificationRate: params.pyneSettings.mediaAmplificationRate,
      highlightPlayButtonEnabled: params.pyneSettings.highlightPlayButton,
      showWatermark: params.pyneSettings.showWatermark,
      showFeedbackButton: params.pyneSettings.showFeedbackButton,
      isCaptionsEnabledByDefault: params.pyneSettings.isCaptionsEnabledByDefault,
    });

    this.checkpoints = this.tourStateStorage.listCheckpoints();

    autorun(() => {
      this.tourStateStorage.setCheckpoints(this.checkpoints);
    });

    reaction(
      () => this.currentStep,
      () => {
        this.shortClipMediaUrl = undefined;
      }
    );

    this.stopListeningPageUrlChange = this.pageInfo.events.on("urlChanged", ({ payload: { url } }) => {
      this.tourStateStorage.set({ lastPageUrl: url });
    });

    this.startFromStepId = params.startFromStepId;
    this.shouldAutoPlayFirstStep = params.shouldAutoPlayFirstStep;
    this.validate();

    this.registerGlobalEventBusMiddleware();
    this.subscribeToAvatarManagerEvents();
    this.subscribeToTourTrackManagerEvents();
    this.subscribeToTourFinishManagerEvents();
    this.subscribeToTourEngagedManagerEvents();

    if (this.pyneSettings.highlightPlayButton && !this.shouldAutoPlayFirstStep) {
      const firstStep = this.tourFlow.sections[0].steps[0];
      this.tourFinishManager.setCurrentStep(firstStep, this.isLastStepInTourFlow(firstStep));
    }
  }

  private setupFlowStateMachine(flow: Flow, initialFlowState: FlowState) {
    this.flowStateActor?.stop();
    this.flowStateStream?.dispose();

    this.flowStateActor = createFlowStateActor({
      input: { flow: flow, initialFlowState: initialFlowState, flowFlexibility: this.flowFlexibility },
      hooks: {
        onSectionTransition: this.handleSectionTransition.bind(this),
        onStepTransition: this.handleStepTransition.bind(this),
        onWaitBlockingStep: this.handleWaitBlockingStep.bind(this),
        onFlowFinish: this.handleFlowFinish.bind(this),
        onStepDispose: this.handleStepDispose.bind(this),
        onStepRun: this.handleStepRun.bind(this),
      },
    });
    this.flowStateStream = fromStream(this.flowStateActor);
    this.flowStateActor?.start();
    const cancel = autorun(() => {
      if (this.avatarManager.isPlaying) {
        this.flowStateActor?.send({ type: "mediaPlaying" });
        cancel();
      }
    });
  }

  private handleWaitBlockingStep(_pos: FlowPosition, step: Step) {
    this.pauseMediaIfStepIsBlocking(step);
  }

  private handleStepTransition(pos: FlowPosition, step: Step) {
    this.tourFinishManager.setCurrentStep(step, this.isLastStepInTourFlow(step));
    this.tourEngagedManager.setCurrentStep(step);
    this.tourTrackManager.setCurrentStep(step);
    this.avatarManager.setCurrentStep(step);
    this.updateTourStateStep(step);
    this.events.emit("stepTransitioned", { step }, this.buildEventContext(pos));
  }

  private handleStepRun(pos: FlowPosition, _step: Step) {
    this.events.emit("stepExecuted", {}, this.buildEventContext(pos));
  }

  private handleStepDispose(_pos: FlowPosition, step: Step) {
    this.setLastCompletedTourStep(step.id);
    this.tourTrackManager.clearCurrentStep();
    this.tourFinishManager.clearCurrentStep();
    this.tourEngagedManager.clearCurrentStep();
  }

  private handleSectionTransition(_pos: FlowPosition, section: Section) {
    this.avatarManager.setCurrentSection(section, section.steps[0], {
      isProgressRingVisible: this.currentFlow?.options.isAvatarProgressRingVisible ?? true,
      autoPlay: true,
      currentTime: 0,
    });
  }

  private handleFlowFinish() {
    this.finishFlow();
  }

  private getWaitingForIntro() {
    const hasWatched = this.tourStateStorage.getHasWatchedIntro();

    if (hasWatched === undefined) {
      return this.settings.opener_type !== "none" && !!this.settings.opener_settings?.title;
    }

    return false;
  }

  validate() {
    if (this.tourPlayback.sections.length === 0) {
      throw new Error("No sections found in the tour playback");
    }

    const isOnlyOneSection = this.tourPlayback.sections.length === 1;
    if (isOnlyOneSection && this.tourPlayback.sections[0].steps.length === 0) {
      throw new Error("No steps found in the tour playback");
    }
  }

  start() {
    this.initializeTourState();

    if (!this.isWaitingForIntro) {
      this.status = "started";
      log("starting tour");

      if (this.startStrategy.type === "startSuspended") {
        this.suspendTour(this.startStrategy.reason, { skipAnalytics: true });
        return;
      }

      if (this.startStrategy.type === "startOver") {
        this.startFlow("tour", { flowState: this.flowState, autoPlay: this.shouldAutoPlayFirstStep });
        this.events.emit("started", {}, this.buildEventContext());
        return;
      }

      if (this.startStrategy.type === "resume") {
        const flowState = this.calculateFlowStateFromStepId(this.startStrategy.stepId);
        this.startFlow("tour", { flowState: flowState, autoPlay: this.shouldAutoPlayFirstStep });
        this.events.emit("started", {}, this.buildEventContext());
        return;
      }

      if (this.startStrategy.type === "bringBack") {
        this.startFlow("wanderingOff");
        this.events.emit("started", {}, this.buildEventContext());
      }
    }
  }

  get flowState(): FlowState {
    const machineFlowState = this.flowStateStream?.current?.context;
    return machineFlowState ?? this.initialFlowState;
  }

  get flowStatus() {
    return this.flowStateStream?.current?.value;
  }

  finishIntro() {
    this.isWaitingForIntro = false;
    this.tourStateStorage.setHasWatchedIntro(true);
    this.start();
  }

  startFlow(flowKey: FlowKey, options?: { autoPlay?: boolean; flowState?: FlowState }) {
    if (flowKey !== "tour") {
      this.flowStateStack.push(this.flowState);
    }

    const defaultOptions = {
      autoPlay: true,
      flowState: this.initialFlowState,
    };

    const { autoPlay, flowState } = merge({}, defaultOptions, options);

    const flow = this.flows[flowKey];
    this.currentFlow = flow;
    this.currentFlowKey = flowKey;

    this.setupFlowStateMachine(flow, flowState);

    this.avatarManager.setCurrentSection(this.currentSection!, this.currentStep!, {
      currentTime: flowState.currentTime,
      autoPlay,
      isProgressRingVisible: flow.options.isAvatarProgressRingVisible,
    });

    this.status = "started";
    this.events.emit("flowStarted", {}, this.buildEventContext());
    log("Flow started", { flowKey, flowState });
  }

  jumpToStepId(stepId: string) {
    const flowState = this.calculateFlowStateFromStepId(stepId);
    this.startFlow("tour", { flowState });
  }

  private setCurrentTime(time: number) {
    this.currentTime = time;
    this.flowStateActor?.send({ type: "timeUpdate", currentTime: time });
  }

  private replayStep(step: Step) {
    this.avatarManager.seekMedia(step.start);
    this.flowStateActor?.send({ type: "replay" });
    this.playIfNotExceeded();
  }

  private playIfNotExceeded() {
    const currentStep = this.currentStep;
    if (!currentStep) return;

    if (this.currentTime < currentStep.end) {
      this.avatarManager.play();
    }
  }

  private isEndOfStepReached() {
    return this.currentStep && this.currentStep.end <= this.currentTime;
  }

  private finishFlow() {
    if (!this.currentFlowKey || !this.currentFlow) return;
    this.events.emit("flowFinished", {}, this.buildEventContext());
    this.currentFlow.hooks?.onFinish?.execute();
  }

  dismissTour() {
    this.status = "dismissed";
    this.avatarManager.terminate();
    this.events.emit("dismissed", {}, this.buildEventContext());
  }

  finishTour() {
    if (this.currentFlowKey !== "tour") {
      return;
    }
    this.status = "finished";
    this.avatarManager.terminate();
    this.events.emit("finished", {}, this.buildEventContext());
  }

  finishCriteriaMet() {
    this.events.emit("finishCriteriaMet", {}, this.buildEventContext());
  }

  engagedCriteriaMet() {
    this.events.emit("userEngaged", {}, this.buildEventContext());
  }

  terminateTour() {
    this.status = "terminated";
    this.clear();
    const eventContext = this.buildEventContext();
    this.avatarManager.terminate();
    this.events.emit("terminated", {}, eventContext);
  }

  switchTour(guideId: string) {
    this.status = "finished";
    this.events.emit("tourSwitched", { guideId });
  }

  pauseTour() {
    this.avatarManager.pause();
  }

  pauseMediaIfStepIsBlocking(step: Step) {
    if (step.isBlocking) {
      this.avatarManager.pause();
    }
  }

  suspendTour(reason: SuspendReason, options: { skipAnalytics: boolean } = { skipAnalytics: false }) {
    this.status = "suspended";
    this.avatarManager.minimize();
    this.tourStateStorage.set({ isSuspended: true, suspendReason: reason });
    this.events.emit("suspended", {}, this.buildEventContext(), { skipGlobal: options.skipAnalytics });
  }

  async unsuspendTour() {
    if (await this.isLastCheckpointOnTrack()) {
      this.tourStateStorage.set({ isSuspended: false });
      await this.loadLastCheckpoint();
      this.events.emit("unsuspended", {}, this.buildEventContext());
      return;
    }

    this.tourStateStorage.set({ isSuspended: false });
    this.events.emit("unsuspended", {}, this.buildEventContext());
    this.startFlow("unsuspend");
  }

  continueTour() {
    this.status = "started";
    this.avatarManager.expand();
    this.unsuspendTour();
  }

  async loadLastCheckpoint() {
    const lastCheckpoint = this.getLastCheckpoint();

    if (!lastCheckpoint) {
      const initialRedirectUrl = this.tourStateStorage.getInitialUrl();
      if (!initialRedirectUrl) {
        reloadWindow();
        return;
      }
      navigateTo(initialRedirectUrl);
      return;
    }

    const isLastCheckpointOnTrack = await this.isLastCheckpointOnTrack();
    if (!isLastCheckpointOnTrack) {
      navigateTo(lastCheckpoint.url);
      return;
    }

    const tourFlowState = this.getLastCheckpointFlowState();
    this.startFlow("tour", { flowState: tourFlowState, autoPlay: true });
    this.events.emit("checkpointLoaded", {}, this.buildEventContext());
  }

  private async isLastCheckpointOnTrack() {
    const lastCheckpoint = this.getLastCheckpoint();
    if (!lastCheckpoint) return false;

    const stepId = lastCheckpoint.stepId;
    const step = this.findTourStepById(stepId);
    if (!step) return false;

    const currentUrl = getUrl();

    const isOnTrack = await waitForOnTrackConditions(step.attributes.onTrackConditions, currentUrl);
    return isOnTrack;
  }

  private findTourStepById(stepId: string) {
    return this.tourSteps.find((step) => step.id === stepId);
  }

  private getLastCheckpointFlowState() {
    const lastCheckpoint = this.getLastCheckpoint();
    if (!lastCheckpoint) return this.initialFlowState;
    return this.calculateFlowStateFromStepId(lastCheckpoint.stepId);
  }

  private getLastCheckpoint() {
    return this.checkpoints[this.checkpoints.length - 1];
  }

  private calculateFlowStateFromStepId(stepId: string) {
    const sectionIndex = this.flows.tour.sections.findIndex((section) =>
      section.steps.some((step) => step.id === stepId)
    );
    const stepIndex = this.flows.tour.sections[sectionIndex].steps.findIndex((step) => step.id === stepId);
    const stepStartTime = this.flows.tour.sections[sectionIndex].steps[stepIndex].start;
    const flowState = {
      currentTime: stepStartTime,
      currentSectionIndex: sectionIndex,
      currentStepIndex: stepIndex,
    };
    return flowState;
  }

  setCurrentTargetElement(el?: Element | null) {
    const nextTarget = el;

    this.runOnTargetElementHighlighted(nextTarget, () => {
      this.events.emit("targetElementHighlighted", {}, this.buildEventContext());
    });

    runInAction(() => {
      this._currentTargetElement = nextTarget;
    });

    if (this._currentTargetElement) {
      this._currentTargetElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }

  getTargetElement(stepId: string) {
    return this.tourTrackManager.getTargetElement(stepId);
  }

  onTargetElementNotFound() {
    if (
      this.currentStep &&
      this.currentStep.isTargetableStepType &&
      (this.currentStep.attributes as any).targetShortClipMediaUrl
    ) {
      this.shortClipMediaUrl = (this.currentStep.attributes as any).targetShortClipMediaUrl;
      this.events.emit("shortClipFallbackTriggered", {}, this.buildEventContext());
      return;
    }
    this.startFlow("targetNotFound");
  }

  onUserWanderedOff() {
    this.events.emit("userWanderedOff", {}, this.buildEventContext());

    const wanderOffStrategy = this.settings.wander_off_strategy ?? "bring_user_back_flow";

    if (wanderOffStrategy === "end_tour") {
      this.finishTour();
    } else {
      this.startFlow("wanderingOff");
    }
  }

  runOnTargetElementHighlighted = debounce(
    (target?: Element | null, callback?: () => void) => {
      if (target) {
        callback?.();
      }
    },
    500,
    { leading: false, trailing: true }
  );

  onQuestionChoiceSelected(choiceId: string) {
    if (!this.currentStep) return;
    if (this.currentStep instanceof MultipleChoiceQuestionStep) {
      const choiceType = this.currentStep.attributes.choices.find((c) => c.id === choiceId)?.type ?? "unknown";
      this.events.emit("questionChoiceSelected", { choiceId, choiceType }, this.buildEventContext());
      this.currentStep.answer(choiceId);
    }
  }

  onModalChoiceSeleceted(choiceId: string) {
    if (!this.currentStep) return;
    if (this.currentStep instanceof MultipleChoiceModalStep) {
      this.currentStep.answer(choiceId);
    }
  }

  onMediaFinish() {
    this.flowStateActor?.send({ type: "mediaFinished" });
  }

  skipCurrentStep() {
    if (!this.currentStep) return;
    this.skipStep(this.currentStep);
  }

  handleShortClipProceed() {
    this.events.emit("shortClipProceedClicked", {}, this.buildEventContext());
    this.forceProceedToNextStep();
  }

  forceProceedToNextStep() {
    if (!this.currentStep) return;
    this.skipStep(this.currentStep);
    this.flowStateActor?.send({ type: "forceProceedToNextStep" });
  }

  skipStep(step: Step) {
    if (this.isLastStepInSection(step)) {
      this.avatarManager.seekMediaToEnd();
      this.avatarManager.play();
      return;
    }

    const nextStep = this.findNextStepInSection(step);
    if (!nextStep) return;

    this.avatarManager.seekMedia(nextStep.start);
    this.avatarManager.play();
  }

  findNextStepInSection(step: Step) {
    const currentSection = this.currentSection;
    if (!currentSection) return null;

    const stepIndex = currentSection.steps.findIndex((s) => s.id === step.id);
    if (stepIndex === -1) return null;
    if (stepIndex === currentSection.steps.length - 1) return null;

    return currentSection.steps[stepIndex + 1];
  }

  isLastStepInSection(step: Step) {
    const currentSection = this.currentSection;
    if (!currentSection) return false;

    const stepIndex = currentSection.steps.findIndex((s) => s.id === step.id);
    if (stepIndex === -1) return false;

    return stepIndex === currentSection.steps.length - 1;
  }

  get prompt() {
    if (!this.currentStep) return null;
    if (this.isPlayingHeadingGap) return null;
    if (this.status === "terminated") return null;
    if (this.status === "suspended") return null;

    return this.currentStep.prompt;
  }

  get modalContent() {
    if (!this.currentStep) return null;
    if (this.isPlayingHeadingGap) return null;
    if (this.status === "terminated") return null;
    if (this.status === "suspended") return null;

    if (!(this.currentStep instanceof MultipleChoiceModalStep)) {
      return null;
    }
    return this.currentStep.modal;
  }

  get currentTargetElement() {
    if (!this.currentStep) return null;
    if (this.isPlayingHeadingGap) return null;
    if (this.status === "terminated") return null;
    if (this.status === "suspended") return null;

    return this._currentTargetElement;
  }

  get additionalContent() {
    if (!this.currentStep) return null;
    if (this.isPlayingGap) return null;
    if (this.status === "terminated") return null;
    if (this.status === "suspended") return null;

    return this.currentStep?.additionalContent;
  }

  get subtitles() {
    if (!this.currentStep) return null;
    if (this.status === "terminated") return null;
    if (this.status === "suspended") return null;
    if (!this.avatarManager.isCaptionsVisible) return null;
    if (this.currentFlowKey !== "tour") return null;

    return this.getSubtitlesForCurrentTime();
  }

  get finishedTourGuideIds() {
    return this.userStateStorage.finishedTourGuideIds;
  }

  get cursorSettings() {
    if (!this.currentStep?.attributes.isHighlightable() || !this.currentTargetElement) {
      return {
        visible: false,
        click: undefined,
      };
    }

    const click = this.currentStep.attributes.virtualCursor.animationType;
    const visible =
      this.currentStep.attributes.virtualCursor.animationType !== "hidden" && !!this._currentTargetElement;

    return {
      visible,
      click,
    };
  }

  get isInitializing() {
    return this.avatarManager.isInitializing;
  }

  get isPlayingGap() {
    if (!this.currentStep) return true;
    return this.isPlayingHeadingGap || this.isPlayingTrailingGap;
  }

  get isPlayingHeadingGap() {
    if (!this.currentStep) return true;
    return this.currentTime < this.currentStep?.start;
  }

  get isPlayingTrailingGap() {
    if (!this.currentStep) return true;
    return this.currentTime > this.currentStep?.end;
  }

  get currentSection(): Section | undefined {
    return this.currentFlow?.sections[this.flowState.currentSectionIndex];
  }

  get currentStep(): Step | undefined {
    return this.currentSection?.steps[this.flowState.currentStepIndex];
  }

  get tourFlow() {
    return this.flows.tour;
  }

  private saveCheckpointBasedOnStrategy(step: Step, url: string) {
    if (this.settings.checkpoint_strategy === "automatic") {
      this.saveCheckpointAutomatic(step, url);
    }

    if (this.settings.checkpoint_strategy === "advanced") {
      this.saveCheckpointAdvanced(step, url);
    }
  }

  // Save one checkpoint per url
  private saveCheckpointAutomatic(step: Step, url: string) {
    const lastCheckpoint = this.getLastCheckpoint();
    if (!lastCheckpoint) {
      this.saveCheckpoint(step, url);
      return;
    }

    const lastCheckpointUrl = lastCheckpoint.url;
    if (lastCheckpointUrl === url) {
      return;
    }

    this.saveCheckpoint(step, url);
  }

  // Configured by the user
  private saveCheckpointAdvanced(step: Step, url: string) {
    if (step.attributes.isCheckpoint) {
      this.saveCheckpoint(step, url);
    }
  }

  private saveCheckpoint(step: Step, url: string) {
    const checkpoint: Checkpoint = { stepId: step.id, stepType: step.attributes.type, url, createdAt: Date.now() };
    this.checkpoints.push(checkpoint);
    this.events.emit("checkpointSaved", { checkpoint }, this.buildEventContext());
  }

  handleProceedNextFromStep() {
    // Temp fix, until we have a better way to handle this
    this.tourTrackManager.stopWatch?.();
    this.seekMediaToEndOfStepIfNotReached();
    this.avatarManager.play();
  }

  private setLastCompletedTourStep(stepId: string) {
    if (this.currentFlowKey !== "tour") return;

    this.tourStateStorage.setLastCompletedStepId(stepId);
  }

  private seekMediaToEndOfStepIfNotReached() {
    if (!this.currentStep) return;
    if (!this.isEndOfStepReached()) {
      this.avatarManager.seekMedia(this.currentStep.end);
    }
  }

  private buildFlows() {
    const systemFlowParams: CreateSystemFlowsParams = {
      unsuspendFlow: {
        resumeTour: this.settings.fillers.resumeTour!,
      },
      targetNotFoundFlow: {
        targetNotFound: this.settings.fillers.targetNotFound!,
      },
      wanderingOffFlow: {
        targetLost: this.settings.fillers.targetLost!,
        resumeTour: this.settings.fillers.resumeTour!,
      },
    };

    return buildAllFlows({
      tourPlayback: this.tourPlayback,
      stepActions: new StepActionsFacade(this),
      flowHookActions: new FlowHookActionsFacade(this),
      flowFlexibility: this.flowFlexibility,
      systemFlowParams,
    });
  }

  private getSubtitlesForCurrentTime(): SubtitleText[][] {
    if (!this.currentSection) return [];

    const transcript = this.currentSection.transcript;
    const subTranscripts = this.divideSubtitles(transcript);
    const currentChunkIndex = subTranscripts.findIndex((subTranscript) => {
      const endTime = subTranscript[subTranscript.length - 1][0];
      return this.currentTime <= endTime;
    });

    if (currentChunkIndex === -1) {
      return [];
    }

    const currentSubTranscript = subTranscripts[currentChunkIndex];
    const words: SubtitleText[] = currentSubTranscript.map(([time, word]) => ({
      word,
      isSpoken: time <= this.currentTime,
    }));

    let charsRead = 0;
    const firstCutoff = words.findIndex(({ word }) => {
      if (charsRead <= 40) {
        charsRead += word.length + 1;
      } else {
        return true;
      }
    });
    const firstLine = words.slice(0, firstCutoff);

    charsRead = 0;
    const secondCutoff = words
      .filter((_, index) => firstCutoff < index)
      .findIndex(({ word }) => {
        if (charsRead <= 35) {
          charsRead += word.length + 1;
        } else {
          return true;
        }
      });
    const secondLine = words.slice(firstCutoff, secondCutoff === -1 ? words.length : secondCutoff);
    const lines = [firstLine, secondLine];

    return lines;
  }

  private updateTourStateStep(step: Step) {
    if (this.currentFlowKey !== "tour") return;

    this.tourStateStorage.set({
      tabId: this.tabId,
      activeStepId: step.id,
      lastStepTransitionTimestamp: Date.now(),
    });
  }

  private initializeTourState() {
    this.tourStateStorage.set({
      activeTourGuideId: this.guideId,
      expirationTimeoutMinutes: this.settings.tour_expiration_timeout,
      tourPlaybackVersion: this.tourPlayback.version,
      lastPageUrl: this.pageInfo.url,
      tabId: this.tabId,
      initialUrl: this.pageInfo.url,
    });
  }

  private divideSubtitles(arr: [number, string][]): [number, string][][] {
    return arr
      .filter(([, str]) => str.trim())
      .reduce((dividedSlices: [number, string][][], [time, text]) => {
        if (
          !dividedSlices.length ||
          dividedSlices[dividedSlices.length - 1].reduce((acc, [, str]) => acc + str.length + 1, 0) + text.length > 75
        ) {
          dividedSlices.push([[time, text]]);
        } else {
          dividedSlices[dividedSlices.length - 1].push([time, text]);
        }
        return dividedSlices;
      }, []);
  }

  private subscribeToAvatarManagerEvents() {
    this.avatarManager.events.on("mediaTimeUpdated", ({ payload: time }) => {
      this.setCurrentTime(time);
    });
    this.avatarManager.events.on("mediaFinished", this.onMediaFinish.bind(this));
    this.avatarManager.events.on("stepReplayed", ({ payload: step }) => {
      this.replayStep(step);
    });
    this.avatarManager.events.on("stepSkipped", ({ payload: step }) => {
      this.skipStep(step);
    });
    this.avatarManager.events.on("minimizeClicked", () => {
      this.suspendTour("userAction");
    });
    this.avatarManager.events.on("expandClicked", () => {
      this.continueTour();
    });
    this.avatarManager.events.on("dismissClicked", () => {
      this.dismissTour();
    });
  }

  private subscribeToTourTrackManagerEvents() {
    this.tourTrackManager.events.on("wanderedOff", ({ payload: step }) => {
      if (this.shouldTriggerFallback(step)) {
        this.onUserWanderedOff();
      }
    });

    this.tourTrackManager.events.on("targetNotFound", ({ payload: { step, reasons } }) => {
      this.events.emit("targetElementNotFound", { reasons }, this.buildEventContext());
      if (this.shouldTriggerFallback(step)) {
        this.onTargetElementNotFound();
      }
    });

    this.tourTrackManager.events.on("trackSatisfied", ({ payload: step }) => {
      this.saveCheckpointBasedOnStrategy(step, getUrl());
    });
  }

  isStepOnTrack(stepId: string) {
    return this.tourTrackManager.isOnTrack(stepId);
  }

  private shouldTriggerFallback(step: Step) {
    return (
      this.flowFlexibility === "strict" || (this.flowFlexibility === "flexible" && step.isBlockingAndTargetableStepType)
    );
  }

  private subscribeToTourFinishManagerEvents() {
    this.tourFinishManager.events.on("finished", () => {
      this.finishCriteriaMet();
    });
  }

  private subscribeToTourEngagedManagerEvents() {
    this.tourEngagedManager.events.on("engaged", () => {
      this.engagedCriteriaMet();
    });
  }

  private registerGlobalEventBusMiddleware() {
    this.stopGlobalEventBusMiddleware = globalEventBus.use("*", (context, type) => {
      if (type.startsWith(TourPlaybackNamespace)) {
        return context;
      }

      return { ...this.buildEventContext(), ...context };
    });
  }

  get isSuspended() {
    return this.status === "suspended";
  }

  get isAudioAvatar() {
    return this.tourPlayback?.mediaType === "audio";
  }

  get tourSteps() {
    return this.tourFlow.sections.flatMap((section) => section.steps);
  }

  get showSubtitles() {
    if (!this.subtitles) return false;
    return this.subtitles.length > 0 && !!this.avatarManager.isPlaying;
  }

  onIntroStartTourClicked() {
    this.events.emit("introStartTourClicked", {}, this.buildEventContext());
    this.finishIntro();
  }

  onIntroCloseTourClicked() {
    this.events.emit("introCloseTourClicked", {}, this.buildEventContext());
    this.dismissTour();
  }

  onIntroPostponeTourClicked() {
    this.events.emit("introPostponeTourClicked", {}, this.buildEventContext());
    this.finishIntro();
    this.suspendTour("userAction");
  }

  private buildFeedbackURLParameter(source: FeedbackSource) {
    const payload = {
      user_id: this.pyneClient?.currentUser?.id,
      anonymous_id: this.pyneClient?.anonymousId ?? undefined,
      organization_id: this.organizationId,
      guide_id: this.guideId,
      metadata: {
        stepIndex: this.flowState.currentStepIndex,
        sessionId: this.sessionId,
        stepType: this.currentStep?.attributes.type,
      },
      source,
    };

    const stringPayload = JSON.stringify(payload);
    try {
      const base64Payload = window.btoa(stringPayload);
      return base64Payload;
    } catch (error) {
      console.error("Error encoding payload", error);
      return null;
    }
  }

  onGiveFeedbackClicked(source: FeedbackSource) {
    window.open(
      `https://dashboard.pyne.ai/feedback?app_id=${this.pyneSettings.app_id}&d=${this.buildFeedbackURLParameter(source)}`,
      "_blank"
    );
    this.events.emit("feedbackClicked", {}, this.buildEventContext());
  }

  private clear() {
    this.stopGlobalEventBusMiddleware?.();
    this.stopListeningPageUrlChange?.();
  }

  private calculateStepOrder(sections: Section[], currentStep: Step) {
    const steps = sections.flatMap((section) => section.steps);
    const idx = steps.findIndex((step) => step.id === currentStep.id);
    return idx !== -1 ? idx : 0;
  }

  private isLastStepInTourFlow(step: Step) {
    const lastSection = this.tourFlow.sections[this.tourFlow.sections.length - 1];
    const lastStep = lastSection.steps[lastSection.steps.length - 1];
    return step.id === lastStep.id;
  }

  private calculateTourProgress(currentStepOrder: number) {
    const totalSteps = this.tourFlow.sections.reduce((acc, section) => acc + section.steps.length, 0);
    return (currentStepOrder / totalSteps) * 100;
  }

  private buildEventContext(pos?: FlowPosition): TourPlaybackEventContext {
    const stepIndex = pos ? pos.currentStepIndex : this.flowState.currentStepIndex;
    const sectionIndex = pos ? pos.currentSectionIndex : this.flowState.currentSectionIndex;

    const currentFlow = pos ? this.flows[pos.flowKey] : this.currentFlow;
    const currentSection = pos ? currentFlow?.sections[pos.currentSectionIndex] : this.currentSection;
    const currentStep = pos ? currentSection?.steps[pos.currentStepIndex] : this.currentStep;

    const currentStepOrder =
      currentFlow && currentStep ? this.calculateStepOrder(currentFlow.sections, currentStep) : 0;
    const tourProgress = this.currentFlowKey === "tour" ? this.calculateTourProgress(currentStepOrder) : 0;

    return {
      totalSteps: currentFlow?.sections.reduce((acc, section) => acc + section.steps.length, 0) ?? 0,
      isCheckpoint: currentStep?.attributes.isCheckpoint ?? false,
      flowKey: currentFlow?.key ?? "",
      stepId: currentStep?.id ?? "",
      stepType: currentStep?.attributes.type ?? "",
      currentMediaDuration: currentSection?.mediaDuration ?? 0,
      stepOrder: currentStepOrder,
      stepIndex: stepIndex,
      sectionIndex: sectionIndex,
      totalSectionSteps: currentSection?.steps.length ?? 0,
      time: this.currentTime,
      totalSections: this.tourFlow.sections.length,
      tourPlaybackVersion: this.tourPlayback.version,
      tourProgress: tourProgress,
      startedAt: this.startedAt,
      sessionId: this.sessionId ?? "",
    };
  }

  get nextSteps() {
    const steps = this.tourFlow.sections.flatMap((section) => section.steps);
    const currentStepIndex = steps.findIndex((step) => step.id === this.currentStep?.id);
    if (currentStepIndex === -1) return steps;

    return steps.slice(currentStepIndex + 1);
  }

  get avatarBackgroundPreviewGifUrl() {
    return this.pyneSettings.avatarBackgroundPreviewGifUrl ?? this.tourPlayback.previewGifUrl;
  }
}

export const AUDIO_ONLY_AVATAR_IMG = `https://base.pyne.ai/storage/v1/object/public/tour-assets/v2/og:96091ece-f265-4685-a8ad-cc9395ec1f5e/g:cc2676a6-e10a-4892-8331-1806cab64e4f/3f43961995cd49e79802b7b768b2f51b/video.gif`;
export const AUDIO_ONLY_MINIMIZED_AVATAR_IMG =
  "https://base.pyne.ai/storage/v1/object/public/tour-assets/v2/og:96091ece-f265-4685-a8ad-cc9395ec1f5e/g:cc2676a6-e10a-4892-8331-1806cab64e4f/3f43961995cd49e79802b7b768b2f51b/thumbnail.jpg";
